import { useState } from 'react';
import { ArrowRight, Trash2 } from 'lucide-react';

import { Button } from 'components/ui/Button';
import { PlateformeDto, PlateformeDtoModeConnexionEnum } from 'api/plateforme';
import { ConfirmationOverlay } from 'components/feedback/ConfirmationOverlay/ConfirmationOverlay';

import { estAdministree as estModeAdministree } from './modeGestion';

interface PlateformeCardProps {
  plateforme: PlateformeDto;
  onClick: () => void;
  onDelete: () => void;
}

/**
 * Carte de la liste des plateformes, centrée sur la gouvernance : la PM
 * gérante identifie la plateforme (« Plateforme gérée par … »), avec sa
 * nature, le thème, le logo et les volumes clés.
 */
const PlateformeCard: React.FC<PlateformeCardProps> = ({ plateforme, onClick, onDelete }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const estAdministree = estModeAdministree(plateforme.modeGestion);
  const estSsoPartenaire = plateforme.modeConnexion === PlateformeDtoModeConnexionEnum.SsoPartenaire;
  const nom = plateforme.gereeParNom || plateforme.name;
  const logoUrl = plateforme.theme?.minimalLogoUrl || plateforme.theme?.logoUrl;
  const swatches = [
    plateforme.theme?.primaryColor,
    plateforme.theme?.secondaryColor,
  ].filter(Boolean) as string[];

  return (
    <div className="flex flex-col rounded-xl border border-gray-100 bg-white shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-lg">
      <div className="flex flex-1 items-start justify-between gap-3 p-5">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
              {estAdministree ? 'Plateforme administrée — titulaire' : 'Plateforme gérée par'}
            </span>
            <span className="rounded-full bg-gray-100 px-2 py-0.5 font-mono text-[11px] font-semibold text-gray-500">
              {plateforme.code}
            </span>
          </div>
          <button
            type="button"
            onClick={onClick}
            className="mt-1 block max-w-full truncate text-left text-lg font-bold text-gray-900 hover:text-primary"
          >
            {nom}
          </button>
          {(plateforme.natureLabel || plateforme.sousNatureLabel) && (
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              {plateforme.natureLabel && (
                <span className="inline-block rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
                  {plateforme.natureLabel}
                </span>
              )}
              {plateforme.sousNatureLabel && (
                <span className="inline-block rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                  {plateforme.sousNatureLabel}
                </span>
              )}
            </div>
          )}
          {swatches.length > 0 && (
            <div className="mt-3 flex items-center gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                Thème
              </span>
              {swatches.map((color, i) => (
                <span
                  key={i}
                  className="h-4 w-4 rounded border border-black/10"
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          )}
        </div>

        {logoUrl ? (
          <div className="flex h-14 w-20 flex-shrink-0 items-center justify-center rounded-lg border border-gray-100 bg-white p-1.5">
            <img src={logoUrl} alt="" className="max-h-full max-w-full object-contain" />
          </div>
        ) : (
          <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10 text-lg font-bold text-primary">
            {nom.charAt(0).toUpperCase()}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 border-t border-gray-100 px-5 py-3">
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          {estAdministree && (
            <span
              className="inline-block truncate rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800"
              title="Le titulaire distribue uniquement les produits autorisés — l'admin pilote la plateforme."
            >
              Administrée par l'admin
            </span>
          )}
          {estSsoPartenaire && (
            <span className="inline-block truncate rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
              SSO partenaire
            </span>
          )}
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          <Button plain onClick={onClick}>
            Ouvrir
            <ArrowRight data-slot="icon" />
          </Button>
          <button
            type="button"
            onClick={() => setShowDeleteConfirm(true)}
            title="Retirer la plateforme"
            className="rounded-lg p-2 text-gray-400 hover:bg-red-50 hover:text-red-600"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      <ConfirmationOverlay
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => {
          setShowDeleteConfirm(false);
          onDelete();
        }}
        title="Retirer la plateforme"
        description={`Confirmer le retrait de la plateforme gérée par ${nom} ? Ses rattachements (courtiers, produits, tunnels) ne seront plus accessibles.`}
        confirmLabel="Retirer"
        cancelLabel="Annuler"
        variant="danger"
      />
    </div>
  );
};

export default PlateformeCard;
